import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Youtube, Instagram, CheckCircle2, Circle } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Loader, ErrorState } from "@/components/states";
import { DemoBadge } from "@/components/DemoBadge";
import { Button } from "@/components/ui/button";

const ICONS = { youtube: Youtube, instagram: Instagram };
const PLATFORM_COLORS = {
  youtube: { bg: "bg-red-500/10", text: "text-red-500", border: "border-red-500/20" },
  instagram: { bg: "bg-pink-500/10", text: "text-pink-500", border: "border-pink-500/20" },
};

function StatusRow({ label, value, good }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1.5">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className={`max-w-[140px] truncate text-right text-xs font-medium ${good ? "text-foreground" : "text-muted-foreground"}`}>{value}</span>
    </div>
  );
}

export default function Connections() {
  const [state, setState] = useState({ loading: true, error: null });
  const [connections, setConnections] = useState([]);
  const [busy, setBusy] = useState(null);
  const [searchParams] = useSearchParams();

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const { data } = await api.get("/connections");
      setConnections(data.connections);
      setState({ loading: false, error: null });
    } catch (err) {
      setState({ loading: false, error: apiErrorMessage(err) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (searchParams.get("youtube") === "connected") toast.success("YouTube connected");
    if (searchParams.get("youtube") === "error") toast.error("YouTube connection was not completed");
    if (searchParams.get("instagram") === "connected") toast.success("Instagram connected");
    if (searchParams.get("instagram") === "error") toast.error("Instagram connection was not completed");
  }, [searchParams]);

  const toggle = async (c) => {
    setBusy(c.platform);
    try {
      if (c.platform === "youtube") {
        if (c.status === "connected" && !c.is_demo) {
          await api.delete("/connections/youtube");
          await load();
          toast.success("YouTube disconnected");
        } else {
          const { data } = await api.get("/connections/youtube/authorize");
          window.location.assign(data.authorization_url);
        }
        return;
      }
      if (c.platform === "instagram") {
        if (c.status === "connected" && !c.is_demo) {
          await api.delete("/connections/instagram");
          await load();
          toast.success("Instagram disconnected");
        } else {
          const { data } = await api.get("/connections/instagram/authorize");
          window.location.assign(data.authorization_url);
        }
        return;
      }
      const connected = c.status === "connected";
      const { data } = await api.post(`/connections/${c.platform}/${connected ? "disconnect" : "connect"}`);
      setConnections((prev) => prev.map((p) => (p.platform === c.platform ? data : p)));
      toast.success(connected ? `${c.display_name} disconnected` : `${c.display_name} connected (demo)`);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally { setBusy(null); }
  };

  return (
    <AppShell title="Connections">
      <div className="mb-8 flex flex-col gap-4 border-b border-border pb-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-accent">Integrations</p>
          <h1 className="mt-2 font-display text-3xl font-bold text-foreground">Connect Platforms</h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Connect your YouTube and Instagram accounts with live OAuth to sync performance analytics and publish content directly.
          </p>
        </div>
      </div>

      {state.loading ? <Loader label="Loading connections" /> :
       state.error ? <ErrorState message={state.error} onRetry={load} /> : (
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="connections-grid">
          {connections.map((c) => {
            const Icon = ICONS[c.platform] || Youtube;
            const connected = c.status === "connected";
            const colors = PLATFORM_COLORS[c.platform] || PLATFORM_COLORS.x;
            return (
              <div key={c.platform}
                className={`rounded-2xl border bg-surface p-5 shadow-sm transition-all ${connected ? `border-opacity-50 ${colors.border}` : "border-border"}`}
                data-testid={`connection-${c.platform}`}>
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${colors.bg}`}>
                      <Icon className={`h-5 w-5 ${colors.text}`} />
                    </span>
                    <span className="font-display font-bold text-foreground">{c.display_name}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {connected ? (
                      <CheckCircle2 className="h-4 w-4 text-accent" />
                    ) : (
                      <Circle className="h-4 w-4 text-muted-foreground/40" />
                    )}
                    <span className={`text-[10px] font-bold uppercase tracking-widest ${connected ? "text-accent" : "text-muted-foreground"}`}
                      data-testid={`status-${c.platform}`}>
                      {connected && c.is_demo ? "Demo" : connected ? "Live" : "Off"}
                    </span>
                  </div>
                </div>

                {/* Stats */}
                <div className="mt-4 border-t border-border pt-3 space-y-0.5">
                  <StatusRow label="Account" value={c.account_name || "—"} good={connected} />
                  <StatusRow label="Last synced" value={c.last_synced_at ? new Date(c.last_synced_at).toLocaleDateString() : "—"} good={connected} />
                  <StatusRow label="Analytics" value={c.analytics_status} good={c.analytics_status === "active"} />
                  <StatusRow label="Publishing" value={c.publishing_status} good={c.publishing_status === "ready"} />
                </div>

                {/* CTA */}
                <div className="mt-4 flex items-center justify-between">
                  {connected && c.is_demo ? <DemoBadge /> : <span />}
                  <Button size="sm" variant={connected ? "outline" : "default"} disabled={busy === c.platform}
                    onClick={() => toggle(c)}
                    className={`rounded-full ${!connected ? "bg-accent text-accent-foreground hover:bg-accent/90" : ""}`}
                    data-testid={`toggle-${c.platform}`}>
                    {busy === c.platform ? "…" :
                     c.platform === "youtube" ? (connected && !c.is_demo ? "Disconnect" : "Connect YouTube") :
                     c.platform === "instagram" ? (connected && !c.is_demo ? "Disconnect" : "Connect Instagram") :
                     connected ? "Disconnect" : "Connect"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
       )}
    </AppShell>
  );
}
